// WORKFLOW runs: one execution of a saved workflow (a DAG of stages, each
// done by a subagent; a fan-out stage runs once per item). Lattice's own
// engine drives them, so every read here is the engine's ledger
// (run.json / events.jsonl / outputs), never the gateway directly.
//
// Polling: the run detail is read every 3 s while the run is live
// (queued / running / paused) and stops by itself once it has ended (the
// run view's live stream replaces it while it is up: api/workflowLive.ts);
// a node's detail every 5 s while that node is live. Every key sits under
// ["workflow-runs", customerId] so an action on one run refreshes the
// runs table (owned by api/workflows.ts) as well as the open run view.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { ApiError, apiFetch } from "@/lib/api";
import type { components } from "@/types/api.gen";

export type RunDetail = components["schemas"]["RunDetail"];
export type RunState = components["schemas"]["RunState"];
export type RunSummary = components["schemas"]["RunSummary"];
export type RunCounters = components["schemas"]["RunCounters"];
export type RunEvent = components["schemas"]["RunEvent"];
export type NodeState = components["schemas"]["NodeState"];
export type NodeResult = components["schemas"]["NodeResult"];
export type NodeDetail = components["schemas"]["NodeDetail"];
export type NodeCheckpoint = components["schemas"]["NodeCheckpoint"];
export type NodeFile = components["schemas"]["NodeFile"];
export type TranscriptLine = components["schemas"]["TranscriptLine"];
export type ReportResponse = components["schemas"]["ReportResponse"];
export type RunActionResponse = components["schemas"]["RunActionResponse"];
export type PromoteRoleBody = components["schemas"]["PromoteRoleBody"];
export type PromoteRoleResponse = components["schemas"]["PromoteRoleResponse"];
export type WorkflowSpec = components["schemas"]["WorkflowSpec"];
export type StageSpec = components["schemas"]["StageSpec"];

export type RunStatus = RunState["status"];
export type NodeStatus = NodeState["status"];

const LIVE_RUN: ReadonlySet<string> = new Set<RunStatus>(["queued", "running", "paused"]);

/** A run the engine is still working on (or holding): worth polling. */
export function isLiveRunStatus(status: string | null | undefined): boolean {
  return !!status && LIVE_RUN.has(status);
}

// ── keys and urls ──

export const workflowRunsKey = (customerId: string | null | undefined) => ["workflow-runs", customerId ?? ""] as const;
export const workflowRunKey = (customerId: string | null | undefined, runId: string | null | undefined) =>
  [...workflowRunsKey(customerId), runId ?? ""] as const;

const base = (customerId: string) => `/api/customers/${encodeURIComponent(customerId)}/workflow-runs`;
const runUrl = (customerId: string, runId: string) => `${base(customerId)}/${encodeURIComponent(runId)}`;
const nodeUrl = (customerId: string, runId: string, nodeId: string) =>
  `${runUrl(customerId, runId)}/nodes/${encodeURIComponent(nodeId)}`;

/** Where one of a node's files downloads from (`name` is the file's own name inside outputs/<node>/). */
export function nodeFileUrl(customerId: string, runId: string, nodeId: string, name: string): string {
  return `${nodeUrl(customerId, runId, nodeId)}/files/${encodeURIComponent(name)}`;
}

/** 404: the run (or the node, or the report) is not there. */
export function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

/** The `{code, message}` the routes put in `detail`, else the error's own text. */
export function actionErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    const detail = (error.body as { detail?: unknown } | null)?.detail;
    if (detail && typeof detail === "object" && typeof (detail as { message?: unknown }).message === "string") {
      return (detail as { message: string }).message;
    }
    return error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong.";
}

// ── reads ──

/**
 * One run, live: the DAG's every node, counters, events. Polled every 3 s
 * while the run is live, unless `poll: false` (the run view's live stream
 * is up and writes the same cache).
 */
export function useWorkflowRun(customerId: string | null | undefined, runId: string | null | undefined, { poll = true }: { poll?: boolean } = {}) {
  const enabled = !!customerId && !!runId;
  return useQuery({
    queryKey: workflowRunKey(customerId, runId),
    enabled,
    queryFn: ({ signal }) => apiFetch<RunDetail>(runUrl(customerId!, runId!), { signal }),
    refetchInterval: (query) => (poll && isLiveRunStatus(query.state.data?.run.status) ? 3_000 : false),
    refetchOnWindowFocus: true,
    staleTime: 2_000,
    retry: (failures, error) => !isNotFound(error) && failures < 1,
  });
}

/**
 * One node's detail: checkpoints, notes tail, result, files, transcript
 * tail. `live` = the node (or its run) is still going: read every 5 s,
 * and once more when it stops (result.md and the files land last).
 */
export function useWorkflowNode(
  customerId: string | null | undefined,
  runId: string | null | undefined,
  nodeId: string | null | undefined,
  { live = false }: { live?: boolean } = {},
) {
  const enabled = !!customerId && !!runId && !!nodeId;
  const q = useQuery({
    queryKey: [...workflowRunKey(customerId, runId), "node", nodeId ?? ""],
    enabled,
    queryFn: ({ signal }) => apiFetch<NodeDetail>(nodeUrl(customerId!, runId!, nodeId!), { signal }),
    refetchInterval: live ? 5_000 : false,
    staleTime: 3_000,
    retry: (failures, error) => !isNotFound(error) && failures < 1,
  });
  const wasLive = useRef(live);
  const { refetch } = q;
  useEffect(() => {
    if (wasLive.current && !live && enabled) void refetch();
    wasLive.current = live;
  }, [live, enabled, refetch]);
  return q;
}

/** The run's report (markdown), once the engine has written it (`report_available` on the detail). */
export function useWorkflowReport(
  customerId: string | null | undefined,
  runId: string | null | undefined,
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: [...workflowRunKey(customerId, runId), "report"],
    enabled: enabled && !!customerId && !!runId,
    queryFn: ({ signal }) => apiFetch<ReportResponse>(`${runUrl(customerId!, runId!)}/report`, { signal }),
    staleTime: 60_000,
    retry: (failures, error) => !isNotFound(error) && failures < 1,
  });
}

// ── actions ──

interface RunArgs {
  customerId: string;
  runId: string;
}

interface NodeArgs extends RunArgs {
  nodeId: string;
}

function useRunAction(action: "cancel" | "pause" | "resume") {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, runId }: RunArgs) =>
      apiFetch<RunActionResponse>(`${runUrl(customerId, runId)}/${action}`, { method: "POST" }),
    // The whole customer prefix: the run view AND the runs table.
    onSettled: (_data, _error, vars) => qc.invalidateQueries({ queryKey: workflowRunsKey(vars.customerId) }),
  });
}

/** Stop the run: every live stage is aborted, what they left running is swept. Confirm first. */
export function useCancelWorkflowRun() {
  return useRunAction("cancel");
}

/** Hold the run: running stages finish, nothing new starts until Resume. */
export function usePauseWorkflowRun() {
  return useRunAction("pause");
}

export function useResumeWorkflowRun() {
  return useRunAction("resume");
}

/** Re-run one failed / timed-out / cancelled / skipped node (and what depends on it) on a fresh attempt. */
/** Save a dynamic-role branch as a permanent subagent (POST …/nodes/{node}/promote). */
export function usePromoteRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, runId, nodeId, body }: NodeArgs & { body: PromoteRoleBody }) =>
      apiFetch<PromoteRoleResponse>(`${nodeUrl(customerId, runId, nodeId)}/promote`, { method: "POST", body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["subagent-blueprints"] }),
  });
}

export function useRetryWorkflowNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, runId, nodeId }: NodeArgs) =>
      apiFetch<RunActionResponse>(`${nodeUrl(customerId, runId, nodeId)}/retry`, { method: "POST" }),
    onSettled: (_data, _error, vars) => qc.invalidateQueries({ queryKey: workflowRunsKey(vars.customerId) }),
  });
}
